import { PencilIcon as PencilIconOutline, TrashIcon, ViewListIcon } from "@heroicons/react/outline";
import {
  PencilIcon as PencilIconSolid,
  ViewListIcon as ViewListIconSolid,
} from "@heroicons/react/solid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import EditorTabContent, { type SkillCollectionSaveAction } from "./editor";
import { SkillCollectionEditorHeader } from "./SkillCollectionEditorHeader";
import { usePageNavigation } from "./hooks/usePageNavigation";
import { useSkillCollectionRollback } from "./hooks/useSkillCollectionRollback";
import { RollbackReviewDialog } from "./RollbackReviewDialog";
import { SkillCollectionHeaderActions } from "./SkillCollectionHeaderActions";
import { SkillCollectionSaveButton } from "./SkillCollectionSaveButton";
import SkillCollectionOverview from "./overview";

import { ConfirmationModal, ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { useDeleteEntity } from "@/components/UnifiedRegistry/Dialogs/hooks/useDeleteEntity";
import BreadcrumbsPageWrapper from "@/components/UnifiedRegistry/BreadcrumbsPageWrapper";
import useFolderPathBreadcrumbs from "@/components/UnifiedRegistry/hooks/useFolderPathBreadcrumbs";
import { ResourceHeader } from "@/components/ui/ResourceHeader";
import type { TabItem } from "@/components/ui/TabNavigation/types";
import { EntityType } from "@/queries/folder-entities/types";
import { useRbac } from "@/providers/RbacProvider";
import {
  useGetSkillCollection,
  useListSkillCollectionVersions,
  useUpdateSkillCollection,
} from "@/queries/skill-collections";
import { SHORTCUTS, isModifierKeyPressed } from "@/utils/shortcuts";

export interface SkillCollectionProps {
  isEditing: boolean;
  workspaceIdNum: number | null;
  collectionIdStr: string | null;
  routeVersionNumber: number | null;
}

const SkillCollection = ({
  isEditing,
  workspaceIdNum,
  collectionIdStr,
  routeVersionNumber,
}: SkillCollectionProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { deleteEntity } = useDeleteEntity();
  const { isEditDisabled } = useRbac();
  const [saveAction, setSaveAction] = useState<SkillCollectionSaveAction | null>(null);
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);
  const { data, error } = useGetSkillCollection(workspaceIdNum, collectionIdStr);
  const versionsQuery = useListSkillCollectionVersions(workspaceIdNum, collectionIdStr);
  const versionsData = versionsQuery.data;
  const updateSkillCollectionMutation = useUpdateSkillCollection();

  const breadcrumbProps = useFolderPathBreadcrumbs(null, data?.skill_collection?.name);
  const versions = useMemo(
    () => versionsData?.pages.flatMap(page => page.versions) ?? [],
    [versionsData?.pages],
  );
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = versionsQuery;
  const versionTotal = versionsData?.pages[0]?.total ?? versions.length;
  const isSkillCollectionEditDisabled = isEditDisabled(EntityType.SKILL_COLLECTION);
  const basePath =
    workspaceIdNum && collectionIdStr
      ? `/workspace/${workspaceIdNum}/skill-collection/${collectionIdStr}`
      : null;
  const latestVersionNumber = versions[0]?.number ?? 1;

  const selectedVersion = useMemo(() => {
    if (routeVersionNumber) {
      return versions.find(v => v.number === routeVersionNumber) ?? null;
    }
    return versions[0] ?? null;
  }, [routeVersionNumber, versions]);

  const {
    rollbackTargetVersion,
    rollbackPhase,
    rollbackReviewItems,
    isRollbackReviewLoading,
    rollbackReviewEmptyMessage,
    restoreMutation,
    handleRequestRollback,
    handleCloseRollbackReview,
    handleConfirmRollback,
  } = useSkillCollectionRollback({
    workspaceIdNum,
    collectionIdStr,
    basePath,
    navigate,
    versions,
  });

  useEffect(() => {
    if (!routeVersionNumber || versions.some(v => v.number === routeVersionNumber)) {
      return;
    }

    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, routeVersionNumber, versions]);

  const selectedTabIndex = isEditing ? 1 : 0;

  const {
    setEditorDirty,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    editorDiscardKey,
    handleTabChange,
    handleDiscardAndLeave,
    handleEditorSaveSuccess,
  } = usePageNavigation({
    basePath,
    isEditing,
    routeVersionNumber,
    selectedVersion,
    latestVersionNumber,
    navigate,
  });

  const handleSaveActionClick = useCallback(() => {
    if (
      !saveAction ||
      saveAction.isSaving ||
      !saveAction.isDirty ||
      isSkillCollectionEditDisabled
    ) {
      return;
    }

    saveAction.onSave();
  }, [isSkillCollectionEditDisabled, saveAction]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isModifierKeyPressed(event) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        handleSaveActionClick();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSaveActionClick, isEditing]);

  const handleTitleEdit = useCallback(
    ({
      newTitle,
      onSuccess,
      onError,
    }: {
      newTitle: string;
      onSuccess?: (newTitle?: string) => void;
      onError?: () => void;
    }) => {
      if (!workspaceIdNum || !collectionIdStr) {
        onError?.();
        return;
      }

      updateSkillCollectionMutation.mutate(
        {
          workspaceId: workspaceIdNum,
          collectionId: collectionIdStr,
          name: newTitle,
        },
        {
          onSuccess: response => onSuccess?.(response.skill_collection.name),
          onError: () => onError?.(),
        },
      );
    },
    [collectionIdStr, updateSkillCollectionMutation, workspaceIdNum],
  );

  const handleDescriptionEdit = useCallback(
    ({
      newDescription,
      onSuccess,
      onError,
    }: {
      newDescription: string | null;
      onSuccess?: (newDescription?: string | null) => void;
      onError?: () => void;
    }) => {
      if (!workspaceIdNum || !collectionIdStr) {
        onError?.();
        return;
      }

      updateSkillCollectionMutation.mutate(
        {
          workspaceId: workspaceIdNum,
          collectionId: collectionIdStr,
          description: newDescription,
        },
        {
          onSuccess: response => onSuccess?.(response.skill_collection.description),
          onError: () => onError?.(),
        },
      );
    },
    [collectionIdStr, updateSkillCollectionMutation, workspaceIdNum],
  );

  const handleDeleteSkillCollection = useCallback(() => {
    if (!workspaceIdNum || !collectionIdStr || !data?.skill_collection) {
      return;
    }

    deleteEntity(
      {
        id: collectionIdStr as unknown as number,
        name: data.skill_collection.name,
        type: EntityType.SKILL_COLLECTION,
        path: [],
      },
      () => {
        navigate(`/workspace/${workspaceIdNum}/home`);
      },
    );
  }, [collectionIdStr, data?.skill_collection, deleteEntity, navigate, workspaceIdNum]);

  if (error) {
    return (
      <BreadcrumbsPageWrapper {...breadcrumbProps} className="flex h-full min-h-0 flex-col">
        <div className="flex h-full items-center justify-center">
          <div className="text-red-500">Failed to load skill collection</div>
        </div>
      </BreadcrumbsPageWrapper>
    );
  }

  if (routeVersionNumber === null && versions.length > 0 && basePath) {
    const latest = versions[0].number;
    const target = isEditing
      ? `${basePath}/version/${latest}/edit`
      : `${basePath}/version/${latest}`;
    return <Navigate to={{ pathname: target, search: location.search }} replace />;
  }

  const collection = data?.skill_collection;
  let saveButtonTooltip = `No changes to save (${SHORTCUTS.SAVE.displayWithPlus})`;

  if (isSkillCollectionEditDisabled) {
    saveButtonTooltip = "You don't have permission to save this skill collection";
  } else if (saveAction?.isDirty) {
    saveButtonTooltip = `Save changes as a new version (${SHORTCUTS.SAVE.displayWithPlus})`;
  }

  const tabs: TabItem[] = [
    {
      id: 0,
      name: "Overview",
      outlineIcon: <ViewListIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
      solidIcon: <ViewListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
      content: !isEditing ? (
        <SkillCollectionOverview
          workspaceId={workspaceIdNum!}
          collectionId={collectionIdStr!}
          rootPath={collection?.root_path ?? ""}
          selectedVersionNumber={routeVersionNumber ?? selectedVersion?.number ?? null}
          selectedVersionFilePaths={selectedVersion?.file_paths ?? []}
          initialSelectedPath={selectedFilePath}
          onSelectedPathChange={setSelectedFilePath}
        />
      ) : null,
    },
    {
      id: 1,
      name: "Editor",
      outlineIcon: <PencilIconOutline className="mr-1.5 h-4 w-4" aria-hidden="true" />,
      solidIcon: <PencilIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
      content: isEditing ? (
        <div className="flex h-full min-h-0 flex-col gap-3 py-3">
          <SkillCollectionEditorHeader
            versionNumber={routeVersionNumber}
            saveButton={
              <SkillCollectionSaveButton
                disabled={
                  isSkillCollectionEditDisabled ||
                  !saveAction?.isDirty ||
                  saveAction?.isSaving === true
                }
                hasUnsavedChanges={saveAction?.isDirty ?? false}
                isSaving={saveAction?.isSaving ?? false}
                onClick={handleSaveActionClick}
                tooltipContent={saveButtonTooltip}
                className="h-[38px] px-4 text-sm"
              />
            }
          />
          <div className="min-h-0 flex-1 overflow-auto">
            <EditorTabContent
              key={`${editorDiscardKey}-${routeVersionNumber}`}
              workspaceId={workspaceIdNum!}
              collectionId={collectionIdStr!}
              rootPath={collection?.root_path ?? ""}
              provider={collection?.provider ?? ""}
              versionNumber={routeVersionNumber}
              initialSelectedPath={selectedFilePath}
              onSelectedPathChange={setSelectedFilePath}
              onDirtyChange={setEditorDirty}
              onSaveActionChange={setSaveAction}
              onSaveSuccess={handleEditorSaveSuccess}
            />
          </div>
        </div>
      ) : null,
    },
  ];

  return (
    <BreadcrumbsPageWrapper
      {...breadcrumbProps}
      className="flex h-full min-h-0 flex-col"
      entityTitle={{
        type: EntityType.SKILL_COLLECTION,
        title: collection?.name ?? "",
        onTitleEdit: handleTitleEdit,
      }}
    >
      <div className="min-h-0 flex-1 overflow-auto">
        <ResourceHeader
          type={EntityType.SKILL_COLLECTION}
          title={collection?.name ?? ""}
          titleInBreadcrumb
          description={collection?.description}
          onTitleEdit={handleTitleEdit}
          onDescriptionEdit={handleDescriptionEdit}
          id={collection?.id ?? ""}
          createdAt={collection?.created_at ?? ""}
          actions={[
            {
              label: "Delete",
              onClick: handleDeleteSkillCollection,
              variant: "destructiveLink",
              disabled: isSkillCollectionEditDisabled,
              tooltipContent: isSkillCollectionEditDisabled
                ? "You don't have permission to delete this skill collection"
                : undefined,
              icon: <TrashIcon className="mr-2 h-4 w-4" />,
            },
          ]}
          versionInfo={
            versionTotal > 0
              ? {
                  current: selectedVersion?.number ?? routeVersionNumber ?? latestVersionNumber,
                  total: versionTotal,
                }
              : undefined
          }
          minimal
          tabsProps={{
            tabs,
            handleTabChange,
            selectedIndex: selectedTabIndex,
            shouldBeSticky: false,
            panelContainerClassName: "min-h-0 flex-1 overflow-hidden",
            panelClassName: "h-full min-h-0 overflow-hidden",
            actionButtons: isEditing ? undefined : (
              <SkillCollectionHeaderActions
                isEditing={isEditing}
                isSkillCollectionEditDisabled={isSkillCollectionEditDisabled}
                saveAction={saveAction}
                saveButtonTooltip={saveButtonTooltip}
                onSaveActionClick={handleSaveActionClick}
                selectedVersion={selectedVersion}
                latestVersionId={versions[0]?.id}
                latestVersionNumber={versions[0]?.number}
                collectionName={data?.skill_collection?.name ?? ""}
                isRestoring={restoreMutation.isPending}
                onRequestRollback={handleRequestRollback}
              />
            ),
          }}
        />
      </div>

      <RollbackReviewDialog
        isOpen={rollbackTargetVersion !== null}
        targetVersionNumber={rollbackTargetVersion?.number ?? null}
        reviewItems={rollbackReviewItems}
        isLoadingReviewItems={isRollbackReviewLoading}
        emptyMessage={rollbackReviewEmptyMessage}
        rollbackPhase={rollbackPhase}
        isRestoring={restoreMutation.isPending}
        onClose={handleCloseRollbackReview}
        onConfirm={handleConfirmRollback}
      />

      <ConfirmationModal
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to leave?"
        isOpen={isConfirmModalOpen}
        setIsOpen={setIsConfirmModalOpen}
        headerVariant={ConfirmationModalVariant.warning}
        leaveButtonText="Discard Changes"
        saveButtonText="Cancel"
        handleOnConfirm={() => setIsConfirmModalOpen(false)}
        handleLeave={handleDiscardAndLeave}
        useBlocker={false}
      />
    </BreadcrumbsPageWrapper>
  );
};

export default SkillCollection;
